import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";

export function useThemePreference() {
  const [systemTheme, setSystemTheme] = useState<"light" | "dark">("light");
  const [preferredTheme, setPreferredTheme] = useState<"light" | "dark" | undefined>(undefined);

  // Detect system theme preference
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    mediaQuery.addEventListener("change", updateSystemTheme);
    updateSystemTheme(mediaQuery);

    return () => mediaQuery.removeEventListener("change", updateSystemTheme);

    function updateSystemTheme(e: MediaQueryListEvent | MediaQueryList) {
      setSystemTheme(e.matches ? "dark" : "light");
    }
  }, []);

  const theme = preferredTheme ?? systemTheme;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const body = document.body;
    if (theme === "dark") {
      body.classList.add("invert-colors");
    } else {
      body.classList.remove("invert-colors");
    }
  }, [theme]);

  // Load theme from localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;
    setPreferredTheme(localStorage["theme"]);
  }, []);

  return {
    theme,
    preferredTheme,
    setTheme: useCallback((theme: Theme | undefined) => {
      setPreferredTheme(theme);
      if (!theme) {
        delete localStorage["theme"];
        return;
      }
      localStorage["theme"] = theme;
    }, []),
  };
}
